import React from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { LOGO_URL } from '../../mock/mock';
import { LayoutDashboard, Train, UtensilsCrossed, ShoppingBag, Users, LogOut, Activity, Settings, Sparkles, Image as ImageIcon, FileText, Car, ShieldAlert } from 'lucide-react';

const AdminLayout = () => {
  const { admin, adminLogout, loading } = useAuth();
  const settings = useSettings();
  const logoSrc = settings?.general?.logo_url || LOGO_URL;
  const siteName = settings?.general?.site_name || 'TrainPanda';
  const nav = useNavigate();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <aside className="w-64 bg-slate-900 flex flex-col fixed inset-y-0 p-4 space-y-4">
          <div className="h-10 bg-slate-800 rounded-xl" />
          <div className="space-y-2 flex-1 pt-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-9 bg-slate-800 rounded-xl" />
            ))}
          </div>
        </aside>
        <main className="flex-1 ml-64 p-8 space-y-6">
          <div className="h-8 w-48 shimmer-wave rounded-lg" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-28 shimmer-wave rounded-2xl" />
            ))}
          </div>
        </main>
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const items = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/sos-alerts', label: '🚨 SOS & Family', icon: ShieldAlert },
    { to: '/admin/trains', label: 'Trains', icon: Train },
    { to: '/admin/restaurants', label: 'Restaurants', icon: UtensilsCrossed },
    { to: '/admin/orders', label: 'Orders', icon: ShoppingBag },
    { to: '/admin/cab-bookings', label: 'Cab Bookings', icon: Car },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/stories', label: 'Stories', icon: Sparkles },
    { to: '/admin/banners', label: 'Banners', icon: ImageIcon },
    { to: '/admin/pages', label: 'Pages', icon: FileText },
    { to: '/admin/scrape-logs', label: 'Scrape Logs', icon: Activity },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-slate-900 text-white flex flex-col fixed inset-y-0 border-r border-slate-800 shadow-xl z-20">
        {/* Brand Header with crisp white logo card */}
        <div className="p-4 border-b border-slate-800/80">
          <div className="bg-white px-3.5 py-2 rounded-xl shadow-sm flex items-center justify-center">
            <img src={logoSrc} alt={siteName} className="h-8 max-w-full object-contain" />
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map(i => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              className={({isActive}) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`
              }
            >
              <i.icon size={17} className="shrink-0" />
              <span>{i.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3.5 border-t border-slate-800/80 bg-slate-950/40">
          <div className="text-xs text-slate-400 px-2 mb-2 flex items-center justify-between">
            <span>Admin</span>
            <b className="text-emerald-400 font-bold bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-800/50">{admin.username}</b>
          </div>
          <button
            onClick={()=>{ adminLogout(); nav('/admin/login'); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer border border-rose-900/30"
          >
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-6 sm:p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
